import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  Layers,
  ChevronDown,
  ChevronRight,
  Eye,
  X,
  SlidersHorizontal,
  Tag
} from 'lucide-react';
import { masterRegistryEntries, registrySections } from '../data/masterRegistryEntries';
import { getFullEncoding, hasFullEncoding } from '../data/fullyEncodedEntries';
import type { RegistryEntry } from '../data/masterRegistryEntries';

type GroupByOption = 'section' | 'shortCode' | 'm3-category' | 'm3-system' | 'none';
type SortByOption = 'name' | 'id' | 'section';

interface SearchFilter {
  id: string;
  type: 'section' | 'm3-category' | 'm3-structure' | 'm3-system' | 'm3-context' | 'm3-attribute' | 'text';
  label: string;
  value: string;
}

export const AdvancedSearch: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<SearchFilter[]>([]);
  const [groupBy, setGroupBy] = useState<GroupByOption>('section');
  const [sortBy, setSortBy] = useState<SortByOption>('name');
  const [showOnlyEncoded, setShowOnlyEncoded] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [showFilterPanel, setShowFilterPanel] = useState(true);

  // Extract all unique M3 values from encoded entries
  const m3Values = useMemo(() => {
    const categories = new Set<string>();
    const structures = new Set<string>();
    const systems = new Set<string>();
    const contexts = new Set<string>();
    const attributes = new Set<string>();

    masterRegistryEntries.forEach(entry => {
      const encoding = getFullEncoding(entry.encodedId);
      if (encoding) {
        encoding.ontology?.category?.forEach(c => categories.add(c));
        encoding.ontology?.structures?.forEach(s => structures.add(s));
        encoding.ontology?.systems?.forEach(sys => systems.add(sys));
        encoding.modality?.contexts?.forEach(ctx => contexts.add(ctx));
        encoding.modality?.attributes?.forEach(attr => attributes.add(attr.attributeType));
      }
    });

    return {
      categories: Array.from(categories).sort(),
      structures: Array.from(structures).sort(),
      systems: Array.from(systems).sort(),
      contexts: Array.from(contexts).sort(),
      attributes: Array.from(attributes).sort()
    };
  }, []);

  const addFilter = (filter: Omit<SearchFilter, 'id'>) => {
    const newFilter: SearchFilter = {
      ...filter,
      id: `${filter.type}-${filter.value}-${Date.now()}`
    };
    setActiveFilters(prev => [...prev, newFilter]);
  };

  const removeFilter = (filterId: string) => {
    setActiveFilters(prev => prev.filter(f => f.id !== filterId));
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setActiveFilters([]);
    setShowOnlyEncoded(false);
  };

  const toggleGroup = (groupId: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const filteredEntries = useMemo(() => {
    return masterRegistryEntries.filter(entry => {
      // Text search
      const matchesSearch = searchTerm === '' || 
        entry.canonicalName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.encodedId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.elements?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.shortCode?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // M3 Encoding filter
      const matchesEncoding = !showOnlyEncoded || hasFullEncoding(entry.encodedId);
      
      // Active filters
      const matchesFilters = activeFilters.every(filter => {
        const encoding = getFullEncoding(entry.encodedId);
        
        switch (filter.type) {
          case 'section':
            return entry.sectionCode === filter.value;
          
          case 'm3-category':
            return encoding?.ontology?.category?.includes(filter.value) || false;
          
          case 'm3-structure':
            return encoding?.ontology?.structures?.includes(filter.value) || false;
          
          case 'm3-system':
            return encoding?.ontology?.systems?.includes(filter.value) || false;
          
          case 'm3-context':
            return encoding?.modality?.contexts?.includes(filter.value) || false;
          
          case 'm3-attribute':
            return encoding?.modality?.attributes?.some(attr => attr.attributeType === filter.value) || false;
          
          default:
            return true;
        }
      });
      
      return matchesSearch && matchesEncoding && matchesFilters;
    });
  }, [searchTerm, activeFilters, showOnlyEncoded]);

  const sortedEntries = useMemo(() => {
    const sorted = [...filteredEntries];
    
    switch (sortBy) {
      case 'name':
        sorted.sort((a, b) => a.canonicalName.localeCompare(b.canonicalName));
        break;
      case 'id':
        sorted.sort((a, b) => a.encodedId.localeCompare(b.encodedId));
        break;
      case 'section':
        sorted.sort((a, b) => a.sectionCode.localeCompare(b.sectionCode));
        break;
    }
    
    return sorted;
  }, [filteredEntries, sortBy]);

  const groupedEntries = useMemo(() => {
    if (groupBy === 'none') {
      return { 'All Entries': sortedEntries };
    }

    const groups: Record<string, RegistryEntry[]> = {};
    
    sortedEntries.forEach(entry => {
      let groupKey: string;
      
      if (groupBy === 'section') {
        const section = registrySections.find(s => s.id === entry.sectionCode);
        groupKey = section?.name || 'Unknown';
      } else if (groupBy === 'shortCode') {
        groupKey = entry.shortCode || 'No Short Code';
      } else if (groupBy === 'm3-category') {
        const encoding = getFullEncoding(entry.encodedId);
        const categories = encoding?.ontology?.category || [];
        groupKey = categories.length > 0 ? categories.join(', ') : 'No M3 Category';
      } else if (groupBy === 'm3-system') {
        const encoding = getFullEncoding(entry.encodedId);
        const systems = encoding?.ontology?.systems || [];
        groupKey = systems.length > 0 ? systems[0] : 'No M3 System';
      } else {
        groupKey = 'All';
      }
      
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(entry);
    });
    
    return groups;
  }, [sortedEntries, groupBy]);

  const getFilterColor = (type: string) => {
    switch (type) {
      case 'section': return 'bg-consulting-slate-blue-100 text-consulting-slate-blue-800 border-consulting-slate-blue-300';
      case 'm3-category': return 'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)]';
      case 'm3-structure': return 'bg-sage-100 text-sage-800 border-sage-300';
      case 'm3-system': return 'bg-sage-100 text-sage-800 border-sage-300';
      case 'm3-context': return 'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)]';
      case 'm3-attribute': return 'bg-coral-100 text-coral-800 border-coral-300';
      default: return 'bg-[var(--hover-tint)] text-charcoal-800 border-[var(--rule)]';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-charcoal-900 mb-2">Advanced Search</h1>
        <p className="text-charcoal-600">
          Multi-faceted search with M3 Meta-Stack filtering, grouping, and sorting
        </p>
      </div>

      {/* Search Bar */}
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-4">
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[var(--text-ter)]" />
          <input
            type="text"
            placeholder="Search by name, ID, elements, or short code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-3 w-full border border-[var(--rule)] focus:ring-2 focus:ring-consulting-slate-blue-500 focus:border-consulting-slate-blue-500"
          />
        </div>

        {/* Active Filter Pills */}
        {activeFilters.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {activeFilters.map(filter => (
              <div
                key={filter.id}
                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${getFilterColor(filter.type)}`}
              >
                <span>{filter.label}</span>
                <button
                  onClick={() => removeFilter(filter.id)}
                  className="hover:bg-black/10 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
            <button
              onClick={clearAllFilters}
              className="text-xs text-charcoal-600 hover:text-charcoal-900 underline"
            >
              Clear all
            </button>
          </div>
        )}

        {/* Controls Row */}
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <button
            onClick={() => setShowFilterPanel(!showFilterPanel)}
            className="inline-flex items-center gap-1 px-3 py-1 bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] rounded text-charcoal-700 font-medium"
          >
            <SlidersHorizontal className="h-4 w-4" />
            {showFilterPanel ? 'Hide' : 'Show'} Filters
          </button>

          <div className="flex items-center gap-2">
            <label className="font-medium text-charcoal-700">Group:</label>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value as GroupByOption)}
              className="px-2 py-1 border border-[var(--rule)] rounded text-sm"
            >
              <option value="section">Section</option>
              <option value="shortCode">Short Code</option>
              <option value="m3-category">M3 Category</option>
              <option value="m3-system">M3 System</option>
              <option value="none">None</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="font-medium text-charcoal-700">Sort:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortByOption)}
              className="px-2 py-1 border border-[var(--rule)] rounded text-sm"
            >
              <option value="name">Name</option>
              <option value="id">ID</option>
              <option value="section">Section</option>
            </select>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showOnlyEncoded}
              onChange={(e) => setShowOnlyEncoded(e.target.checked)}
              className="rounded border-[var(--rule)] text-sage-600 focus:ring-sage-500"
            />
            <Layers className="h-4 w-4 text-sage-600" />
            <span className={showOnlyEncoded ? 'font-medium text-charcoal-900' : 'text-charcoal-700'}>
              M3 Encoded Only
            </span>
          </label>

          <div className="ml-auto text-charcoal-600">
            <span className="font-bold text-charcoal-900">{filteredEntries.length}</span> {filteredEntries.length === 1 ? 'result' : 'results'}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-6">
        {/* Filter Panel */}
        {showFilterPanel && (
          <div className="md:col-span-1 space-y-4">
            {/* Section Filters */}
            <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-4">
              <h3 className="font-bold text-charcoal-900 mb-3 flex items-center gap-2">
                <Tag className="h-4 w-4" />
                Registry Sections
              </h3>
              <div className="space-y-1.5">
                {registrySections.map(section => (
                  <button
                    key={section.id}
                    onClick={() => addFilter({
                      type: 'section',
                      label: section.name,
                      value: section.id
                    })}
                    className="w-full text-left px-3 py-1.5 text-sm rounded hover:bg-consulting-slate-blue-50 text-charcoal-700 hover:text-consulting-slate-blue-900 transition-colors"
                  >
                    {section.name}
                  </button>
                ))}
              </div>
            </div>

            {/* M3: Ontology Filters */}
            <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-4">
              <h3 className="font-bold text-charcoal-900 mb-3 flex items-center gap-2">
                <Layers className="h-4 w-4 text-[var(--mustard)]" />
                M3: Ontology
              </h3>
              
              <div className="space-y-3">
                <div>
                  <h4 className="text-xs font-semibold text-charcoal-600 uppercase mb-1.5">Categories</h4>
                  <div className="space-y-1">
                    {m3Values.categories.map(cat => (
                      <button
                        key={cat}
                        onClick={() => addFilter({
                          type: 'm3-category',
                          label: `Category: ${cat}`,
                          value: cat
                        })}
                        className="w-full text-left px-2 py-1 text-xs rounded hover:bg-[var(--mustard-dim)] text-charcoal-700 hover:text-[var(--mustard)] transition-colors"
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-xs font-semibold text-charcoal-600 uppercase mb-1.5">Structures</h4>
                  <div className="space-y-1">
                    {m3Values.structures.map(struct => (
                      <button
                        key={struct}
                        onClick={() => addFilter({
                          type: 'm3-structure',
                          label: `Structure: ${struct}`,
                          value: struct
                        })}
                        className="w-full text-left px-2 py-1 text-xs rounded hover:bg-sage-50 text-charcoal-700 hover:text-sage-900 transition-colors"
                      >
                        {struct}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-xs font-semibold text-charcoal-600 uppercase mb-1.5">Systems</h4>
                  <div className="space-y-1">
                    {m3Values.systems.slice(0, 8).map(sys => (
                      <button
                        key={sys}
                        onClick={() => addFilter({
                          type: 'm3-system',
                          label: `System: ${sys}`,
                          value: sys
                        })}
                        className="w-full text-left px-2 py-1 text-xs rounded hover:bg-sage-50 text-charcoal-700 hover:text-sage-900 transition-colors truncate"
                        title={sys}
                      >
                        {sys}
                      </button>
                    ))}
                    {m3Values.systems.length > 8 && (
                      <div className="text-xs text-[var(--text-ter)] px-2 py-1">
                        +{m3Values.systems.length - 8} more
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* M3: Modality Filters */}
            <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-4">
              <h3 className="font-bold text-charcoal-900 mb-3 flex items-center gap-2">
                <Layers className="h-4 w-4 text-[var(--mustard)]" />
                M3: Modality
              </h3>
              
              <div className="space-y-3">
                <div>
                  <h4 className="text-xs font-semibold text-charcoal-600 uppercase mb-1.5">Contexts</h4>
                  <div className="space-y-1">
                    {m3Values.contexts.map(ctx => (
                      <button
                        key={ctx}
                        onClick={() => addFilter({
                          type: 'm3-context',
                          label: `Context: ${ctx}`,
                          value: ctx
                        })}
                        className="w-full text-left px-2 py-1 text-xs rounded hover:bg-[var(--mustard-dim)] text-charcoal-700 hover:text-[var(--mustard)] transition-colors"
                      >
                        {ctx}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-xs font-semibold text-charcoal-600 uppercase mb-1.5">Attributes</h4>
                  <div className="space-y-1">
                    {m3Values.attributes.slice(0, 6).map(attr => (
                      <button
                        key={attr}
                        onClick={() => addFilter({
                          type: 'm3-attribute',
                          label: `Attribute: ${attr}`,
                          value: attr
                        })}
                        className="w-full text-left px-2 py-1 text-xs rounded hover:bg-coral-50 text-charcoal-700 hover:text-coral-900 transition-colors truncate"
                        title={attr}
                      >
                        {attr}
                      </button>
                    ))}
                    {m3Values.attributes.length > 6 && (
                      <div className="text-xs text-[var(--text-ter)] px-2 py-1">
                        +{m3Values.attributes.length - 6} more
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Results Area */}
        <div className={showFilterPanel ? 'md:col-span-3' : 'md:col-span-4'}>
          {Object.keys(groupedEntries).length === 0 ? (
            <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-12 text-center">
              <Search className="h-12 w-12 text-[var(--text-ter)] mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-charcoal-900 mb-2">No Results Found</h3>
              <p className="text-charcoal-600">
                Try adjusting your search criteria or filters
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedEntries).map(([groupName, entries]) => (
                <div key={groupName} className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] overflow-hidden">
                  {groupBy !== 'none' && (
                    <button
                      onClick={() => toggleGroup(groupName)}
                      className="w-full px-4 py-3 bg-[var(--surface)] flex items-center justify-between hover:bg-[var(--hover-tint)] transition-colors border-b"
                    >
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-charcoal-900">{groupName}</h3>
                        <span className="px-2 py-1 bg-[var(--hover-tint)] text-charcoal-700 rounded-full text-xs font-medium">
                          {entries.length}
                        </span>
                      </div>
                      {expandedGroups.has(groupName) ? (
                        <ChevronDown className="h-5 w-5 text-charcoal-600" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-charcoal-600" />
                      )}
                    </button>
                  )}
                  
                  {(groupBy === 'none' || expandedGroups.has(groupName)) && (
                    <div className="divide-y">
                      {entries.map((entry) => {
                        const section = registrySections.find(s => s.id === entry.sectionCode);
                        const isEncoded = hasFullEncoding(entry.encodedId);
                        
                        return (
                          <div key={entry.encodedId} className="p-4 hover:bg-[var(--surface)] transition-colors">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h4 className="font-semibold text-charcoal-900">{entry.canonicalName}</h4>
                                  {isEncoded && (
                                    <Layers className="h-4 w-4 text-sage-600" title="Has M3 Encoding" />
                                  )}
                                </div>
                                <div className="flex items-center gap-2 mb-2 text-sm flex-wrap">
                                  <span className={`px-2 py-1 rounded text-xs font-medium bg-${section?.color || 'gray'}-100 text-${section?.color || 'gray'}-800`}>
                                    {entry.sectionCode.toUpperCase()}
                                  </span>
                                  <span className="font-mono text-charcoal-600 text-xs">{entry.encodedId}</span>
                                  {entry.shortCode && (
                                    <span className="px-2 py-1 bg-[var(--hover-tint)] text-charcoal-700 rounded text-xs font-semibold">
                                      {entry.shortCode}
                                    </span>
                                  )}
                                </div>
                                {entry.elements && (
                                  <p className="text-sm text-charcoal-600 line-clamp-2">
                                    {entry.elements}
                                  </p>
                                )}
                              </div>
                              <Link
                                to={`/elevation/entry/${entry.encodedId}`}
                                className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1 text-sm text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 hover:bg-consulting-slate-blue-50 rounded transition-colors"
                              >
                                <Eye className="h-4 w-4" />
                                View
                              </Link>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};